import { useMutation, useQuery } from '@apollo/client/react';
import { type FunctionComponent } from 'react';
import {
	type MarkAllNotificationsReadMutation,
	type NotificationActivityType,
	type NotificationDisabledStatusQuery,
	type NotificationsQuery,
	type NotificationsQueryVariables
} from '../../../__generated__/graphql-client-types';
import { ACCOUNT_NOTIFICATION_SETTINGS, PROJECT_ONBOARDING_LINK, SHOP_BY_LOOK_LINK } from '../../../constants/links';
import { buildGTMNotificationTabClick } from '../../../helpers/analytics/gtm/event-builders';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { useCustomer } from '../../../hooks/apollo/customer/customer.hooks';
import { useUnreadProjectNotificationCount } from '../../../hooks/customer-notification/customer-notification.hooks';
import { NOTIFICATION_DISABLED_STATUS } from '../../../queries/ecrm/account-details/account-details.queries';
import { MARK_ALL_NOTIFICATIONS_READ, NOTIFICATIONS } from '../../../queries/notifications/notifications.queries';
import { type NotificationContent } from '../../../types/customer.types';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { Drawer } from '../../common-components/drawer/drawer.component';
import { Link } from '../../common-components/link/link.component';
import { SectionListContainer } from '../../common-components/section-list-container/section-list-container.component';
import { Tabs, Tab } from '../../common-components/tabs/tabs.component';
import { Notifications, OptInContent } from '../../notifications/notifications.component';
import { GuestContentView } from '../../project-components/guest-content-view/guest-content-view.component';
import ShopByLookSvg from '../../svgs/shop-by-look.svg';
import SettingsIcon from '../../svgs/untitled-ui/settings.svg';
import XCloseIcon from '../../svgs/untitled-ui/x-close.svg';

const defaultTabClasses = 'ma1 pv2 ph3 w-100 f6 br2';
const tabClasses = `${defaultTabClasses} hover-bg-theme-grey-lighter`;
const tabActiveClasses = `${defaultTabClasses} bg-theme-primary-lighter`;

const ZeroNotificationsContent: FunctionComponent = () => {
	return (
		<div className="flex flex-column pv3 ph4">
			<span className="f4 lh-title font-medium">You currently have no notifications</span>
			<ShopByLookSvg className="pv4 w-100" style={{ height: 150 }} />
			<div className="pb3">
				<span className="f4 lh-title font-medium">Need some inspiration?</span>
				<p className="mb2">Search products, styles, and rooms curated by designers to help you build your project.</p>
			</div>
			<div className="flex flex-column">
				<LinkButton url={SHOP_BY_LOOK_LINK}>Shop by Look</LinkButton>
				<span className="pt3" />
				<LinkButton buttonStyle="SECONDARY" url={PROJECT_ONBOARDING_LINK}>
					Start a Project
				</LinkButton>
			</div>
		</div>
	);
};

type ProjectDropdownProps = {
	name: string;
	notifications: { notifications: NotificationContent[]; totalPages: number };
	projectId: number;
};

const ProjectDropdown: FunctionComponent<ProjectDropdownProps> = ({ name, notifications, projectId }) => {
	const { unreadProjectNotificationCount } = useUnreadProjectNotificationCount(projectId);

	return (
		<div className="ph2 pb1">
			<SectionListContainer name={name} badgeCount={unreadProjectNotificationCount} startOpen={false}>
				<div className="bg-theme-white br2">
					<Notifications notificationData={notifications} loading={false} limitHeight={true} />
				</div>
			</SectionListContainer>
		</div>
	);
};

type NotificationsListProps = {
	type?: NotificationActivityType;
};

const NotificationsList: FunctionComponent<NotificationsListProps> = ({ type }) => {
	const activityTypes = type ? [type] : null;
	const variables: NotificationsQueryVariables = {
		input: { paging: { useInfiniteScroll: true }, activityTypes, markRead: false }
	};
	const { data, loading, fetchMore } = useQuery<NotificationsQuery, NotificationsQueryVariables>(NOTIFICATIONS, {
		variables
	});
	const { data: { notificationDisabledStatus: areNotificationsDisabled } = {} } =
		useQuery<NotificationDisabledStatusQuery>(NOTIFICATION_DISABLED_STATUS);
	const notificationData = data?.customer.notifications;
	const hasZeroNotifications = notificationData && notificationData.notifications.length === 0;

	if (areNotificationsDisabled) {
		return <OptInContent showHeading={true} />;
	}

	if (!type && hasZeroNotifications) {
		return <ZeroNotificationsContent />;
	}

	const notificationsByProject = notificationData?.notifications.reduce<
		Record<string, { notifications: NotificationContent[]; projectId: number; totalPages: number }>
	>((memo, notification) => {
		const projectName = notification.projectName;
		if (!projectName) {
			return memo;
		}
		if (memo[projectName]) {
			memo[projectName].notifications.push(notification);
		} else {
			memo[projectName] = { notifications: [notification], projectId: notification.projectId, totalPages: 1 };
		}
		return memo;
	}, {});

	if (!hasZeroNotifications && type === 'PROJECT') {
		return (
			<div className="pb6">
				{notificationsByProject
					? Object.keys(notificationsByProject).map((project) => (
							<div key={project}>
								<ProjectDropdown
									name={project}
									notifications={notificationsByProject[project]}
									projectId={notificationsByProject[project].projectId}
								/>
							</div>
					  ))
					: null}
			</div>
		);
	}

	return <Notifications notificationData={notificationData} loading={loading} fetchMore={fetchMore} variables={variables} />;
};

const NotificationFeedHeader: FunctionComponent = () => (
	<div className="flex items-center justify-between">
		<Link url={ACCOUNT_NOTIFICATION_SETTINGS} analyticsEvent={{ data: buildGTMNotificationTabClick('settings') }} className="flex">
			<SettingsIcon className="theme-white pl2" />
		</Link>
		<span className="flex-grow-1">Notifications</span>
	</div>
);

export const NotificationFeedTabs: FunctionComponent = () => {
	const { hasAccount } = useCustomer();
	const trackEvent = useTrackEvent();
	const { data } = useQuery<NotificationsQuery, NotificationsQueryVariables>(NOTIFICATIONS, {
		variables: { input: { paging: { useInfiniteScroll: false }, markRead: false } }
	});
	const notificationData = data?.customer.notifications;
	const hasNotificationData = notificationData && notificationData.notifications.length > 0;
	const isTabDisabled = !hasAccount || !hasNotificationData;

	const onTabClick = (type: 'all' | 'orders' | 'projects') => {
		trackEvent(buildGTMNotificationTabClick(type));
	};
	return (
		<Tabs startingTabName="All" tabListClasses="flex justify-around tc ph2 pt2 pb1" tabControlClasses="bn" tabStyle="NONE">
			<Tab tabClasses={tabClasses} tabActiveClasses={tabActiveClasses} name="All" onClick={() => onTabClick('all')}>
				<GuestContentView
					hasAccount={hasAccount}
					content={<NotificationsList />}
					message="to get notifications for your orders and projects"
				/>
			</Tab>
			<Tab
				tabClasses={tabClasses}
				tabActiveClasses={tabActiveClasses}
				name="Orders"
				isDisabled={isTabDisabled}
				onClick={() => onTabClick('orders')}>
				<NotificationsList type="GROUP" />
			</Tab>
			<Tab
				tabClasses={tabClasses}
				tabActiveClasses={tabActiveClasses}
				name="Projects"
				isDisabled={isTabDisabled}
				onClick={() => onTabClick('projects')}>
				<NotificationsList type="PROJECT" />
			</Tab>
		</Tabs>
	);
};

export type NotificationFeedProps = {
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NotificationFeed: FunctionComponent<NotificationFeedProps> = ({ setIsOpen }) => {
	const { refetch } = useCustomer();
	const [markAllNotificationsRead] = useMutation<MarkAllNotificationsReadMutation>(MARK_ALL_NOTIFICATIONS_READ);

	const handleClose = async () => {
		setIsOpen(false);
		await markAllNotificationsRead();
		await refetch();
	};

	return (
		<Drawer
			ariaLabel="Notification Feed"
			direction="right"
			showOverlay={true}
			className="z-5 overflow-y-scroll"
			title={<NotificationFeedHeader />}
			titleClasses="bg-theme-primary theme-white tc mb0 pv2 text-[2em]"
			onClose={handleClose}
			customCloseButton={
				<div
					role="button"
					aria-label="close"
					className="absolute right-0 top-0 theme-white"
					style={{ marginTop: '0.4rem' }}
					tabIndex={0}>
					<XCloseIcon className="pa2" />
				</div>
			}>
			<NotificationFeedTabs />
		</Drawer>
	);
};
